import {ChangeEvent, ReactElement, useState, useContext} from 'react'
import {
  FormControl,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
} from '@material-ui/core'
import {Trans} from 'react-i18next'
import {DfnsDepositCryptoAssets} from '@bdswiss/common-enums'

import AppContext from '../../../../../../Common/contexts/AppContext'
import messages from '../../../../../../../assets/messages'
import {CRYPTO_FIELD_NAMES, CRYPTO_DEFAULT_FIELD_VALUES} from '../../../constants'
import {useStyles} from './styles'

type CryptoFieldsProps = {
  networkOptions: {value: string; label: string}[]
  cryptoPaymentFields: Record<string, any> | null
  onCryptoSet: (fieldValues) => void
  onClose: () => void
}

function CryptoFields(props: CryptoFieldsProps): ReactElement {
  const {cryptoPaymentFields, onCryptoSet, onClose, networkOptions} = props
  const [submitError, setSubmitError] = useState<{
    i18nKey: string
    defaults: string
  } | null>(null)

  const {companyObject} = useContext(AppContext)
  const classes = useStyles()
  const [formFieldsValues, setFormFieldsValues] = useState(
    cryptoPaymentFields || CRYPTO_DEFAULT_FIELD_VALUES,
  )
  const isSubmitDisabled = Object.values(formFieldsValues).some((value) => {
    if (typeof value === 'string') return !value.trim()
    return !value
  })

  const handleTextFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
    const {value, name} = event.target
    setFormFieldsValues((prevValues) => ({...prevValues, [name]: value}))
  }

  const handleSelectFieldChange = (
    event: ChangeEvent<{name?: string | undefined; value: unknown}>,
  ) => {
    const {value, name} = event.target
    if (!name || !value) return

    setFormFieldsValues((prevValues) => ({...prevValues, [name]: value}))
  }

  const handleCheckboxFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
    const {name} = event.target
    setFormFieldsValues((prevValues) => ({...prevValues, [name]: !prevValues[name]}))
  }

  const handleSave = () => {
    const isAddressValid = validateWalletAddress(
      formFieldsValues.walletAddress,
      formFieldsValues.cryptoNetwork,
    )
    if (!isAddressValid) return void setSubmitError(messages.walletAddressError)

    const isConfirmAddressMatched =
      formFieldsValues.walletAddress === formFieldsValues.walletAddressConfirmation
    if (!isConfirmAddressMatched)
      return void setSubmitError(messages.walletAddressConfirmationError)

    onCryptoSet(formFieldsValues)
    onClose()
  }

  return (
    <>
      <FormControl fullWidth>
        <TextField
          type="text"
          name={CRYPTO_FIELD_NAMES.walletAddress}
          value={formFieldsValues.walletAddress}
          label={<Trans {...messages.walletAddress} />}
          onChange={handleTextFieldChange}
        />
      </FormControl>
      <FormControl fullWidth>
        <TextField
          type="text"
          name={CRYPTO_FIELD_NAMES.walletAddressConfirmation}
          value={formFieldsValues.walletAddressConfirmation}
          label={<Trans {...messages.walletAddressConfirmation} />}
          onChange={handleTextFieldChange}
        />
      </FormControl>
      <FormControl fullWidth>
        <InputLabel>
          <Trans {...messages.network} />
        </InputLabel>
        <Select
          value={formFieldsValues.cryptoNetwork}
          name={CRYPTO_FIELD_NAMES.cryptoNetwork}
          onChange={handleSelectFieldChange}
        >
          {networkOptions.map(({value, label}) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControlLabel
        classes={{label: classes.checkboxLabel}}
        control={
          <Checkbox
            checked={formFieldsValues.walletResponsibilityConfirmation}
            name={CRYPTO_FIELD_NAMES.walletResponsibilityConfirmation}
            onChange={handleCheckboxFieldChange}
            color="primary"
          />
        }
        label={
          <Trans
            {...messages.walletResponsibilityConfirmation}
            values={{company: companyObject.brandLabel}}
          />
        }
      />
      {submitError && (
        <Typography color="error" variant="caption" className={classes.checkboxLabel}>
          <Trans {...submitError} />
        </Typography>
      )}
      <Button
        disabled={isSubmitDisabled}
        variant="contained"
        color="primary"
        onClick={handleSave}
        className={classes.button}
      >
        <Trans {...messages.saveAndContinue} />
      </Button>
    </>
  )
}

function validateWalletAddress(address: string, networkOption: string): boolean {
  switch (networkOption) {
    case DfnsDepositCryptoAssets['USDT/ERC20'].value:
    case DfnsDepositCryptoAssets['USDT/BEP20'].value:
    case DfnsDepositCryptoAssets['USDC/ERC20'].value:
      return /^0x[0-9a-fA-F]{40}$/.test(address.trim())
    case DfnsDepositCryptoAssets['USDT/TRC20'].value:
      return /^T[1-9A-HJ-NP-Za-km-z]{33}$/.test(address.trim())
    default:
      return true
  }
}

export {CryptoFields}
